import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useCart} from '../context/CartContext';
import {useAuth} from '../context/AuthContext';
import {useMerchant} from '../context/MerchantContext';
import {customerApi, merchantApi, orderApi} from '../services/api';
import {geocodeAddress} from '../services/geolocationService';
import {DeliveryMethod, Order, OrderStatus, PaymentMethod, PAYMENT_METHOD_DESCRIPTIONS} from '../types';
import { formatPhone } from '../utils/formatters';
import { getCheckoutPaymentMethodIcon, getCheckoutPaymentMethodImageSrc, getCheckoutPaymentMethodLabel, getCheckoutPaymentMethods } from '../utils/paymentMethods';
import CurrencyInput from './common/CurrencyInput';
import { ref, onValue } from 'firebase/database';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, rtdb } from '../config/firebase';
import OrderProcessingAnimation from './OrderProcessingAnimation';
import { getSalesRecoveryCartCode, resetSalesRecoveryCartCode } from '../services/salesRecoveryService';
import {
  BackButton,
  CloseButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle
} from './common/modal/ModalComponents';
import Modal from './common/modal/Modal';
import toast from 'react-hot-toast';
import { trackProductsPurchased } from '../services/SuggestionService';

import {
  AddressDetailRow,
  AddressInfo,
  AddressSummary,
  ChangeInfoSection,
  ChangeText,
  CashbackBadge,
  CashbackSection,
  CashbackHeader,
  CashbackInputWrapper,
  CheckoutButton,
  ClosedMessage,
  ConfirmationSection,
  CouponCard,
  CouponCardContent,
  DetailContent,
  DetailSection,
  DetailTitle,
  EditButton,
  EditChangeButton,
  InputGroup,
  Label,
  LoginPrompt,
  NewStatusCircle,
  NewStatusInfo,
  NewStatusLabel,
  NewStatusStep,
  NewStatusStepper,
  NewStatusTime,
  OrderDetailCard,
  OrderItemRow,
  PaymentActionButton,
  PaymentActions,
  PaymentOption,
  PaymentOptionInfo,
  PaymentSummaryRow,
  CashbackButton,
  CashbackSectionNote,
  ScheduledInfo,
  SectionTitle,
  Select,
  StatusBadgeContainer,
  StatusContainer,
  StatusHeader,
  StatusHelpButton,
  StatusHelpSection,
  StatusHelpTitle,
  StatusSubtitle,
  StatusTitle,
  StepCircle,
  OrderIdBox,
  StatusPrintHeader,
  DeliveryEstimateBox,
  StatusTimelineContainer,
  VerticalTimeline,
  TimelineSteps,
  TimelineStepItem,
  StepPoint,
  StepTime,
  StepDesc,
  StepIndicator,
  StepItem,
  StepLabel,
  StepLine,
  StepCard,
  SummaryRow,
  SummarySection,
  TextArea,
  TwoPaymentsButton,
  WizardContainer,
  StepIndicatorWrapper,
  OrderHeaderCard,
  OrderCodeBadge,
  ConfirmationCodeContainer,
  ConfirmationCodeTitle,
  ConfirmationCodeValue,
  ConfirmationCodeLabel,
  WhatsAppPromptActions,
  WhatsAppPromptButton,
  WhatsAppPromptContent,
  WhatsAppPromptIcon,
  WhatsAppPromptText,
  WhatsAppPromptTitle
} from './CheckoutModalStyles';

interface CheckoutModalProps {
  zIndex?: number;
}

const CUSTOMER_BLOCKED_FOR_ORDERING = 'CUSTOMER_BLOCKED_FOR_ORDERING';
const CUSTOMER_BLOCKED_ORDERING_MESSAGE = 'Você não pode fazer mais compras neste cardápio. Entre em contato com a loja.';

const getCheckoutErrorCode = (error: unknown): string | undefined => {
  const responseData = (error as any)?.response?.data;
  return responseData?.errorCode;
};

const renderPaymentMethodIcon = (methodId: PaymentMethod | string | null | undefined, color?: string): React.ReactNode => {
  const imageSrc = getCheckoutPaymentMethodImageSrc(methodId);

  if (imageSrc) {
    return (
      <img
        alt=""
        aria-hidden="true"
        src={imageSrc}
        style={{
          width: '22px',
          height: '22px',
          flexShrink: 0,
          objectFit: 'contain',
          display: 'block',
          color,
        }}
      />
    );
  }

  return <i className={`fas ${getCheckoutPaymentMethodIcon(methodId)}`} style={color ? { color } : undefined}></i>;
};

const CheckoutModal: React.FC<CheckoutModalProps> = ({ zIndex }) => {
  const { 
    isCheckoutModalOpen, 
    setIsCheckoutModalOpen, 
    restaurant, 
    merchantId,
    deliveryOption, 
    setDeliveryOption,
    scheduledDate,
    setScheduledDate,
    scheduledTime,
    setScheduledTime,
    selectedAddress,
    selectedOrder,
    setSelectedOrder,
    shouldRecalculateFee,
    setShouldRecalculateFee,
    setShowAddressModal,
    setIsCouponModalOpen,
    setProductModalData,
    refreshAvailability,
    hasSeenSuggestions,
    setHasSeenSuggestions
  } = useMerchant();
  const { state: cartState, clearCart, setCashbackBalance, applyCashback, removeCashback, removeCoupon, setDeliveryFee: setCartDeliveryFee } = useCart();
  const { user, isAuthenticated, setShowLoginModal, updateUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(PaymentMethod.CREDIT_CARD_OFFLINE);
  const [paymentMethod2, setPaymentMethod2] = useState<PaymentMethod | null>(null);
  const [paymentAmount1, setPaymentAmount1] = useState<number>(0);
  const [paymentAmount2, setPaymentAmount2] = useState<number>(0);
  const [isTwoPayments, setIsTwoPayments] = useState(false);
  const [showTwoPaymentsSelection, setShowTwoPaymentsSelection] = useState(false);
  const [currentSelectingPayment, setCurrentSelectingPayment] = useState<1 | 2>(1);
  
  const [needsChange, setNeedsChange] = useState<boolean | null>(null);
  const [changeFor, setChangeFor] = useState<string>('');
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingFee, setIsCalculatingFee] = useState(false);
  const [deliveryEstimatedTime, setDeliveryEstimatedTime] = useState<string | null>(null);
  
  const [isScheduling, setIsScheduling] = useState(false);
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false);
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [cashbackInputValue, setCashbackInputValue] = useState<number>(0);
  
  const [needsChange2, setNeedsChange2] = useState<boolean | null>(null);
  const [changeFor2, setChangeFor2] = useState<string>('');
  const [isChangeModalOpen2, setIsChangeModalOpen2] = useState(false);

  const [lastOrder, setLastOrder] = useState<Order | null>(null);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [whatsappOrderUrl, setWhatsappOrderUrl] = useState<string | null>(null);
  const [isWhatsAppPromptOpen, setIsWhatsAppPromptOpen] = useState(false);

  const lastCalculatedAddressRef = useRef<string>('');
  const availablePaymentMethods = useMemo(() => getCheckoutPaymentMethods(restaurant), [restaurant]);
  const defaultPaymentMethod = availablePaymentMethods[0]?.id ?? PaymentMethod.CREDIT_CARD_OFFLINE;

  // Load user data when modal opens
  useEffect(() => {
    if (isCheckoutModalOpen && isAuthenticated && restaurant?.id) {
      const loadBalance = async () => {
        try {
          const balance = await customerApi.getWalletBalance(restaurant.id);
          updateUser({
            availableCashbackBalance: balance.availableBalance,
            pendingCashbackBalance: balance.pendingCashback
          });
          setCashbackBalance(balance.availableBalance);
        } catch (error) {
          console.error('Error loading cashback balance:', error);
        }
      };
      loadBalance();
    }
  }, [isCheckoutModalOpen, isAuthenticated, restaurant?.id]);

  // Sync with selectedOrder from context
  useEffect(() => {
    if (selectedOrder) {
      setLastOrder(selectedOrder);
      setCurrentStep(5);
    }
  }, [selectedOrder]);

  // Utils: data/hora curto DD/MM/YY H:MM
  const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  
  const formatDateTimeShort = (value: string | Date | null | undefined): string => {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const dd = pad2(d.getDate());
    const mm = pad2(d.getMonth() + 1);
    const yy = `${d.getFullYear()}`.slice(-2);
    const hh = d.getHours(); // hora sem zero à esquerda
    const min = pad2(d.getMinutes());
    return `${dd}/${mm}/${yy} ${hh}:${min}`;
  };

  const formatMaybeIso = (value?: string | null): string => {
    if (!value) return '';
    // Se parece ISO, formatar; caso contrário, manter (ex.: "30-45 min")
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return formatDateTimeShort(value);
    }
    return value;
  };

  const getStatusRank = (status?: string | OrderStatus) => {
    if (isOrderCancelled(status)) return 100;

    const order = [
      OrderStatus.PROCESSING,
      OrderStatus.CREATED,
      OrderStatus.RECEIVED,
      OrderStatus.CHECK_AVAILABILITY,
      OrderStatus.PROCESS_PAYMENT,
      OrderStatus.PAYMENT_CONFIRMED,
      OrderStatus.CONFIRMED,
      OrderStatus.ACCEPTED,
      OrderStatus.READY_FOR_TAKEAWAY,
      OrderStatus.READY_FOR_DELIVERY,
      OrderStatus.OUT_FOR_DELIVERY,
      OrderStatus.DELIVERED
    ];

    const index = order.indexOf(status as OrderStatus);
    return index === -1 ? 0 : index;
  };

  const mergeOrderUpdate = (current: Order | null, incoming: Partial<Order> & { status?: string }) => {
    if (!current) return incoming as Order;

    const currentRank = getStatusRank(current.status);
    const incomingRank = getStatusRank(incoming.status);
    const status = incoming.status && incomingRank >= currentRank
      ? incoming.status
      : current.status;

    return {
      ...current,
      ...incoming,
      id: current.id,
      status: status as OrderStatus
    } as Order;
  };

  const canReviewOrder = (order?: Order | null) =>
    order?.status === OrderStatus.DELIVERED &&
    !!order.reviewInvitation?.token &&
    !order.reviewInvitation.alreadyReviewed &&
    !order.reviewInvitation.expired;

  const openOrderReview = (order?: Order | null) => {
    if (!order?.reviewInvitation?.token) return;
    window.location.assign(`/avaliar/${order.reviewInvitation.token}`);
  };

  // Observe last order via Firebase. REST is used only as a fallback/complement.
  useEffect(() => {
    if (currentStep !== 5 || !lastOrder?.id || !restaurant?.id) {
      return;
    }

    const orderId = lastOrder.id;
    const unsubscribers: Array<() => void> = [];
    let pollingInterval: NodeJS.Timeout | null = null;
    let disposed = false;

    const applyFirebaseOrderUpdate = async (source: string, data: any) => {
      if (!data || disposed) return;

      const firebaseOrder = { ...data, id: orderId } as Partial<Order> & { status?: string };
      console.debug('[OrderStatus] live update', source, {
        orderId,
        status: firebaseOrder.status,
        data
      });

      setLastOrder(prev => mergeOrderUpdate(prev, firebaseOrder));

      try {
        const fullOrder = await orderApi.getOrder(restaurant.id, orderId);
        if (!disposed) {
          setLastOrder(prev => mergeOrderUpdate(prev, { ...fullOrder, ...firebaseOrder }));
        }
      } catch (err) {
        console.error('[OrderStatus] Error refreshing order details after Firebase update:', err);
      }
    };

    if (rtdb && user?.id) {
      const rtdbPath = `customers/${user.id}/${orderId}`;
      const unsubscribe = onValue(
        ref(rtdb, rtdbPath),
        snapshot => applyFirebaseOrderUpdate(`rtdb:${rtdbPath}`, snapshot.val()),
        error => console.error('[OrderStatus] RTDB lastOrder listener error:', error)
      );
      unsubscribers.push(unsubscribe);
    }

    if (db) {
      const firestorePath = `orders/${orderId}`;
      const unsubscribe = onSnapshot(
        doc(db, 'orders', orderId),
        snapshot => {
          if (!snapshot.exists()) {
            console.debug('[OrderStatus] Firestore order document not found yet', { path: firestorePath });
            return;
          }
          applyFirebaseOrderUpdate(`firestore:${firestorePath}`, snapshot.data());
        },
        error => console.error('[OrderStatus] Firestore lastOrder listener error:', error)
      );
      unsubscribers.push(unsubscribe);
    }

    if (!rtdb && !db) {
      console.warn('[OrderStatus] Firebase is not configured. Falling back to REST polling for order status.');
      pollingInterval = setInterval(async () => {
        try {
          const order = await orderApi.getOrder(restaurant.id, orderId);
          if (!disposed) {
            setLastOrder(prev => mergeOrderUpdate(prev, order));
          }
        } catch (err) {
          console.error('[OrderStatus] REST polling error:', err);
        }
      }, 5000);
    }

    return () => {
      disposed = true;
      unsubscribers.forEach(unsubscribe => unsubscribe());
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [currentStep, lastOrder?.id, restaurant?.id, user?.id]);

  // Observe pending order via Firebase
  useEffect(() => {
    if (!pendingOrderId || !restaurant?.id) {
      return;
    }

    const statusToFinalize = ['CREATED', 'RECEIVED', 'ACCEPTED', 'CONFIRMED'];
    const statusToCancel = ['ERROR', 'CANCELLED'];
    const unsubscribers: Array<() => void> = [];
    let pollingInterval: NodeJS.Timeout | null = null;
    let finished = false;

    const handleOrderStatusUpdate = async (source: string, data: any) => {
      if (!data || finished) return;

      const status = data.status;
      console.debug('[OrderStatus]', source, {
        orderId: pendingOrderId,
        status,
        data
      });

      if (status === 'PROCESSING') setProcessingProgress(40);
      else if (status === 'CREATED') setProcessingProgress(60);
      else if (status === 'RECEIVED') setProcessingProgress(80);
      else if (status === 'ACCEPTED') setProcessingProgress(90);
      else if (status === 'CONFIRMED') setProcessingProgress(95);

      if (statusToCancel.includes(status) || String(status || '').startsWith('CANCELLED_')) {
        finished = true;
        const fullOrder = await orderApi.getOrder(restaurant.id, pendingOrderId);
        finalizeOrder(mergeOrderUpdate(fullOrder, { ...data, status }));
        return;
      }

      if (statusToFinalize.includes(status)) {
        try {
          const fullOrder = await orderApi.getOrder(restaurant.id, pendingOrderId);
          finished = true;
          finalizeOrder(mergeOrderUpdate(fullOrder, { ...data, status }));
        } catch (err) {
          console.error('[OrderStatus] Error fetching full order after status update:', err);
          finished = true;
          finalizeOrder({ ...data, id: pendingOrderId } as Order);
        }
      }
    };

    console.debug('[OrderStatus] Starting listeners', {
      orderId: pendingOrderId,
      restaurantId: restaurant.id,
      userId: user?.id,
      hasRealtimeDatabase: !!rtdb,
      hasFirestore: !!db
    });

    if (rtdb && user?.id) {
      const rtdbPath = `customers/${user.id}/${pendingOrderId}`;
      const orderRef = ref(rtdb, rtdbPath);
      setProcessingProgress(30);

      const unsubscribe = onValue(
        orderRef,
        snapshot => handleOrderStatusUpdate(`rtdb:${rtdbPath}`, snapshot.val()),
        error => console.error('[OrderStatus] RTDB listener error:', error)
      );
      unsubscribers.push(unsubscribe);
    }

    if (db) {
      const firestorePath = `orders/${pendingOrderId}`;
      const unsubscribe = onSnapshot(
        doc(db, 'orders', pendingOrderId),
        snapshot => {
          if (!snapshot.exists()) {
            console.debug('[OrderStatus] Firestore document not found yet', { path: firestorePath });
            return;
          }
          handleOrderStatusUpdate(`firestore:${firestorePath}`, {
            ...snapshot.data(),
            id: snapshot.id
          });
        },
        error => console.error('[OrderStatus] Firestore listener error:', error)
      );
      unsubscribers.push(unsubscribe);
    }

    if (!rtdb && !db) {
      console.warn('[OrderStatus] Firebase is not configured in this build. Falling back to REST polling.');
    }

    // Fallback de polling enquanto o pedido está em processamento. Reduzido de
    // 5s para 2.5s: quando o Firebase não entrega o evento (build sem config ou
    // listener ainda conectando), esta é a única fonte de atualização do status
    // na tela de "processando", então um intervalo menor melhora a percepção.
    pollingInterval = setInterval(async () => {
      if (finished) return;

      try {
        const order = await orderApi.getOrder(restaurant.id, pendingOrderId);
        await handleOrderStatusUpdate('rest-polling', order);
      } catch (err) {
        console.error('[OrderStatus] REST polling error:', err);
      }
    }, 2500);

    return () => {
      finished = true;
      unsubscribers.forEach(unsubscribe => unsubscribe());
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pendingOrderId, restaurant?.id, user?.id]);

  // Calculate delivery fee based on selected address
  useEffect(() => {
    const calculateFee = async () => {
      if (deliveryOption === 'delivery' && selectedAddress && restaurant?.id && (shouldRecalculateFee || lastCalculatedAddressRef.current !== `${selectedAddress.zipCode || ''}-${selectedAddress.street || ''}-${selectedAddress.number || ''}`)) {
        const addressKey = `${selectedAddress.zipCode || ''}-${selectedAddress.street || ''}-${selectedAddress.number || ''}`;
        
        setIsCalculatingFee(true);
        try {
          lastCalculatedAddressRef.current = addressKey;
          // First get coordinates from address
          const coords = await geocodeAddress(selectedAddress);
          if (coords && coords.latitude && coords.longitude) {
            // Then calculate delivery fee using coordinates
            const deliveryInfo = await merchantApi.calculateDeliveryFee(restaurant.id, coords.latitude, coords.longitude);
            const fee = typeof deliveryInfo.fee === 'number' ? deliveryInfo.fee : parseFloat(String(deliveryInfo.fee));
            setCartDeliveryFee(fee);
            if (deliveryInfo.minTime && deliveryInfo.maxTime) {
              setDeliveryEstimatedTime(`${deliveryInfo.minTime}-${deliveryInfo.maxTime} min`);
            } else if (deliveryInfo.maxTime) {
              setDeliveryEstimatedTime(`${deliveryInfo.maxTime} min`);
            }
          } else {
            // Fallback to local calculation if geocoding fails
            const configs = restaurant.deliveryOptions?.deliveryRadiusConfig;
            if (configs && configs.length > 0) {
              const fee = typeof configs[0].fee === 'number' ? configs[0].fee : parseFloat(String(configs[0].fee));
              setCartDeliveryFee(fee);
              setDeliveryEstimatedTime(configs[0].estimatedTime);
            }
          }
        } catch (error) {
          console.error('Error calculating delivery fee:', error);
          // Fallback to local calculation
          const configs = restaurant.deliveryOptions?.deliveryRadiusConfig;
          if (configs && configs.length > 0) {
            const fee = typeof configs[0].fee === 'number' ? configs[0].fee : parseFloat(String(configs[0].fee));
            setCartDeliveryFee(fee);
            setDeliveryEstimatedTime(configs[0].estimatedTime);
          }
        } finally {
          setIsCalculatingFee(false);
          setShouldRecalculateFee(false);
        }
      } else if (deliveryOption !== 'delivery') {
        setCartDeliveryFee(0);
      }
    };

    calculateFee();
  }, [deliveryOption, selectedAddress, restaurant?.id, setCartDeliveryFee, shouldRecalculateFee, setShouldRecalculateFee]);

  const isRestaurantOpen = restaurant?.isOpen ?? restaurant?.enabled; // Prioritize isOpen, fallback to enabled
  const deliveryOptions = restaurant?.deliveryOptions;
  const schedulingEnabled = deliveryOptions?.schedulingEnabled ?? restaurant?.schedulingEnabled ?? false;

  const subtotal = cartState.items.reduce((total, item) => total + (Number(item.price) * Number(item.quantity)), 0);
  const minimumOrderValue = deliveryOptions?.minimumOrderValue || 0;
  const isBelowMinimum = subtotal < minimumOrderValue;
  
  const deliveryFee = deliveryOption === 'takeaway' ? 0 : (Number(cartState.deliveryFee) || 0);
  
  const currentCouponDiscount = cartState.coupon ? 
    (cartState.coupon.discountAmount !== undefined ? Number(cartState.coupon.discountAmount) :
    (Number(cartState.coupon.discountValue) || 
     (cartState.coupon.discountPercentage ? (Number(subtotal) * Number(cartState.coupon.discountPercentage) / 100) : 0)))
    : 0;

  // Valor máximo de cashback permitido
  let maxAllowedCashback = user?.availableCashbackBalance || 0;
  const valueBeforeCashback = Math.max(0, (Number(subtotal) || 0) + (Number(deliveryFee) || 0) - currentCouponDiscount);

  if (restaurant?.cashbackConfig) {
    const config = restaurant.cashbackConfig;
    if (config.maxCashbackPerOrder !== undefined && config.maxCashbackPerOrder > 0) {
      maxAllowedCashback = Math.min(maxAllowedCashback, config.maxCashbackPerOrder);
    }
    if (config.maxUsagePercentagePerOrder !== undefined && config.maxUsagePercentagePerOrder > 0) {
      const maxByPercentage = (valueBeforeCashback * config.maxUsagePercentagePerOrder) / 100;
      maxAllowedCashback = Math.min(maxAllowedCashback, maxByPercentage);
    }
  }
  // Não pode usar mais cashback do que o valor do pedido (após cupom)
  maxAllowedCashback = Math.min(maxAllowedCashback, valueBeforeCashback);

  const discount = (Number(cartState.appliedCashback) || 0) + currentCouponDiscount;
  const total = Math.max(0, (Number(subtotal) || 0) + (Number(deliveryFee) || 0) - (Number(discount) || 0));

  // Cashback que o usuário ganhará com este pedido
  const cashbackEarned = restaurant?.cashbackConfig?.enabled ? (
    restaurant.cashbackConfig.type === 'PERCENTAGE' 
      ? (total * restaurant.cashbackConfig.value / 100)
      : restaurant.cashbackConfig.value
  ) : 0;

  useEffect(() => {
    if (cartState.appliedCashback > maxAllowedCashback && cartState.appliedCashback > 0) {
      removeCashback();
      toast.error('O cashback aplicado foi removido pois excede as regras do pedido atual.');
    }
  }, [maxAllowedCashback, cartState.appliedCashback, removeCashback]);

  useEffect(() => {
    if (isCheckoutModalOpen) {
      setPaymentMethod(prevPaymentMethod =>
        prevPaymentMethod && availablePaymentMethods.some(method => method.id === prevPaymentMethod)
          ? prevPaymentMethod
          : defaultPaymentMethod
      );
      if (restaurant && !isRestaurantOpen && restaurant.schedulingEnabled) {
        setIsScheduling(true);
      }
      // Trigger fee calculation when modal opens if address is already selected
      if (deliveryOption === 'delivery' && selectedAddress) {
        setShouldRecalculateFee(true);
      }
    }
    if (!isCheckoutModalOpen) {
      setCurrentStep(1);
      setIsTwoPayments(false);
      setPaymentMethod(defaultPaymentMethod);
      setPaymentMethod2(null);
      setShowTwoPaymentsSelection(false);
      setIsEditingAmount(false);
      setIsScheduling(false);
      setScheduledDate(null);
      setScheduledTime(null);
      setLastOrder(null);
      setSelectedOrder(null);
      setWhatsappOrderUrl(null);
      setIsWhatsAppPromptOpen(false);
    }
  }, [isCheckoutModalOpen, availablePaymentMethods, defaultPaymentMethod, isRestaurantOpen, restaurant]);

  useEffect(() => {
    if (isTwoPayments && total > 0) {
      if (paymentAmount1 === 0 && paymentAmount2 === 0) {
         setPaymentAmount1(total / 2);
         setPaymentAmount2(total / 2);
      }
    }
  }, [isTwoPayments, total, paymentAmount1, paymentAmount2]);

  if (!isCheckoutModalOpen) return null;

  const handleNextStep = () => {
    if (currentStep === 2) {
      if (cartState.appliedCashback > maxAllowedCashback) {
        toast.error(`O valor de cashback aplicado (${formatCurrency(cartState.appliedCashback)}) excede o limite permitido para este pedido.`);
        return;
      }
    }
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleClose = () => {
    setIsCheckoutModalOpen(false);
    setSelectedOrder(null);
    setIsWhatsAppPromptOpen(false);
    setWhatsappOrderUrl(null);
    setCurrentStep(1);
    setLastOrder(null);
  };

  const buildWhatsAppOrderUrl = (order: any) => {
    const whatsappNumber = restaurant?.contact?.socialMedia?.whatsapp || restaurant?.contact?.phone;
    if (!whatsappNumber) return null;

    let cleanNumber = whatsappNumber.replace(/\D/g, '');
    if (cleanNumber.length === 10 || cleanNumber.length === 11) {
      cleanNumber = '55' + cleanNumber;
    }
    
    // Formatting the message
    let message = `*PEDIDO #${order.orderDisplayId || order.id?.substring(0, 8)}*\n`;
    message += `------------------------------\n`;
    message += `*Cliente:* ${user?.name || 'Não informado'}\n`;
    message += `*Telefone:* ${user?.phone || 'Não informado'}\n`;
    message += `------------------------------\n\n`;
    
    message += `*ITENS:*\n`;
    cartState.items.forEach((item: any) => {
      message += `${item.quantity}x ${item.name} - ${formatCurrency(item.price * item.quantity)}\n`;
      if (item.options && item.options.length > 0) {
        item.options.forEach((opt: any) => {
          message += `  + ${opt.name}${opt.price ? ` (${formatCurrency(opt.price)})` : ''}\n`;
        });
      }
      if (item.notes) {
        message += `  _Obs: ${item.notes}_\n`;
      }
    });
    
    message += `\n------------------------------\n`;
    message += `*Subtotal:* ${formatCurrency(subtotal)}\n`;
    if (deliveryOption === 'delivery') {
      message += `*Taxa de Entrega:* ${deliveryFee > 0 ? formatCurrency(deliveryFee) : 'Grátis'}\n`;
    }
    if (discount > 0) {
      message += `*Desconto:* -${formatCurrency(discount)}\n`;
    }
    message += `*TOTAL: ${formatCurrency(total)}*\n`;
    message += `------------------------------\n\n`;
    
    message += `*ENTREGA:* ${deliveryOption === 'delivery' ? 'Delivery' : 'Retirada'}\n`;
    if (deliveryOption === 'delivery' && selectedAddress) {
      message += `*Endereço:* ${selectedAddress.street}, ${selectedAddress.number}${selectedAddress.complement ? ` - ${selectedAddress.complement}` : ''}\n`;
      message += `${selectedAddress.neighborhood}, ${selectedAddress.city}-${selectedAddress.state}\n`;
    }
    
    if (isScheduling && scheduledDate && scheduledTime) {
      message += `*Agendado para:* ${scheduledDate} às ${scheduledTime}\n`;
    }

    message += `\n*PAGAMENTO:* \n`;
    if (isTwoPayments) {
      message += `1. ${getCheckoutPaymentMethodLabel(paymentMethod)}: ${formatCurrency(paymentAmount1)}\n`;
      if (paymentMethod === PaymentMethod.CASH) {
        message += `   Troco para: ${needsChange ? formatCurrency(Number(changeFor)) : 'Não precisa'}\n`;
      }
      message += `2. ${getCheckoutPaymentMethodLabel(paymentMethod2)}: ${formatCurrency(paymentAmount2)}\n`;
      if (paymentMethod2 === PaymentMethod.CASH) {
        message += `   Troco para: ${needsChange2 ? formatCurrency(Number(changeFor2)) : 'Não precisa'}\n`;
      }
    } else {
      message += `${getCheckoutPaymentMethodLabel(paymentMethod)}\n`;
      if (paymentMethod === PaymentMethod.CASH) {
        message += `Troco para: ${needsChange ? formatCurrency(Number(changeFor)) : 'Não precisa'}\n`;
      }
    }
    
    if (notes) {
      message += `\n*OBSERVAÇÕES GERAIS:* \n${notes}\n`;
    }

    return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
  };

  const isOrderCancelled = (status?: OrderStatus | string) => {
    if (!status) return false;
    return [
      OrderStatus.CANCELLED,
      OrderStatus.CANCELLED_BY_MERCHANT,
      OrderStatus.CANCELLED_BY_CUSTOMER,
      OrderStatus.CANCELLED_BY_CHECK_AVAILABILITY,
      OrderStatus.CANCELLED_BY_TIMEOUT,
      OrderStatus.ERROR
    ].includes(status as OrderStatus);
  };

  const finalizeOrder = (order: Order) => {
    setLastOrder(order);
    setPendingOrderId(null);
    setProcessingProgress(100);

    if (isOrderCancelled(order.status)) {
      toast.error('Ocorreu um problema com seu pedido.');
      return;
    }

    toast.success('Pedido realizado com sucesso!');

    const whatsappUrl = buildWhatsAppOrderUrl(order);
    if (whatsappUrl) {
      setWhatsappOrderUrl(whatsappUrl);
      setIsWhatsAppPromptOpen(true);
    }
    
    // Limpar todas as variáveis do pedido após sucesso
    clearCart();
    setPaymentMethod(restaurant?.paymentMethods?.[0]?.id as PaymentMethod || PaymentMethod.CREDIT_CARD_OFFLINE);
    setPaymentMethod2(null);
    setPaymentAmount1(0);
    setPaymentAmount2(0);
    setIsTwoPayments(false);
    setNeedsChange(null);
    setChangeFor('');
    setNeedsChange2(null);
    setChangeFor2('');
    setNotes('');
    setIsScheduling(false);
    setScheduledDate(null);
    setScheduledTime(null);
    setCashbackInputValue(0);
    setDeliveryEstimatedTime(null);
  };

  const handleOpenOrderWhatsApp = () => {
    if (!whatsappOrderUrl) return;

    window.open(whatsappOrderUrl, '_blank', 'noopener,noreferrer');
    setIsWhatsAppPromptOpen(false);
  };

  const handleSubmit = async () => {
    if (!restaurant?.id) return;

    // Verificar disponibilidade em tempo real antes de prosseguir (sem cache)
    setIsSubmitting(true);
    try {
      const isAvailable = await refreshAvailability();
      if (!isAvailable && !isScheduling) {
        toast.error('Desculpe, a loja fechou enquanto você montava seu pedido.');
        setIsSubmitting(false);
        return;
      }
    } catch (e) {
      console.error('Error checking availability before submit:', e);
      // Se falhar a verificação, prosseguimos mas com cautela
    }
    
    if (isBelowMinimum) {
      toast.error(`O valor mínimo para pedido é ${formatCurrency(minimumOrderValue)}`);
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Helper to format scheduled date/time to ISO string if needed, 
      // or just pass as is if the backend expects a specific format.
      // openapi.yaml says format: date-time, so it should be ISO.
      let scheduledTo = undefined;
      if (isScheduling && scheduledDate && scheduledTime) {
        // scheduledDate format: "segunda-feira, 26/01/2026"
        // scheduledTime format: "17:00 - 17:30"
        try {
          const datePart = scheduledDate.split(', ')[1]; // "26/01/2026"
          const [day, month, year] = datePart.split('/');
          const timePart = scheduledTime.split(' - ')[0]; // "17:00"
          const [hours, minutes] = timePart.split(':');
          
          const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes));
          scheduledTo = date.toISOString();
        } catch (e) {
          console.error('Error parsing scheduled date:', e);
        }
      }

      const orderData = {
        items: cartState.items,
        customer: {
          id: user?.id,
          name: user?.name || '',
          phone: user?.phone || '',
          email: user?.email || '',
        },
        total,
        isTwoPayments,
        paymentMethod,
        paymentMethod2,
        paymentAmount1: isTwoPayments ? paymentAmount1 : total,
        paymentAmount2: isTwoPayments ? paymentAmount2 : 0,
        needsChange,
        changeFor,
        needsChange2,
        changeFor2,
        deliveryMethod: deliveryOption === 'takeaway' ? DeliveryMethod.TAKEAWAY : DeliveryMethod.DELIVERY,
        cartCode: getSalesRecoveryCartCode(),
        discount,
        coupon: cartState.coupon ? cartState.coupon.code : null,
        cashbackUsedAmount: cartState.appliedCashback,
        cashbackEarnedAmount: cashbackEarned,
        address: selectedAddress,
        deliveryFee,
        notes,
        scheduledTo,
        estimatedTime: deliveryOption === 'takeaway' ? '20-30 min' : isScheduling ? 'Agendado' : '45-60 min'
      };

      // Validar checkout se usar cupom ou cashback
      if (orderData.coupon || (orderData.cashbackUsedAmount && orderData.cashbackUsedAmount > 0)) {
        try {
          const validation = await orderApi.validateCheckout(restaurant.id, orderData);
          if (!validation.isValid) {
            const errorMessage = validation.errorCodes?.includes(CUSTOMER_BLOCKED_FOR_ORDERING)
                ? CUSTOMER_BLOCKED_ORDERING_MESSAGE
                : validation.issues && validation.issues.length > 0
                ? validation.issues.join('. ')
                : 'Não foi possível validar o cupom ou cashback.';
            toast.error(errorMessage);
            setIsSubmitting(false);
            return;
          }
        } catch (valError) {
          console.error('Error validating checkout:', valError);
          toast.error('Erro ao validar cupom ou cashback. Tente novamente.');
          setIsSubmitting(false);
          return;
        }
      }

      const response = await orderApi.createOrder(restaurant.id, orderData);
      const orderId = response.id;
      resetSalesRecoveryCartCode();
      
      // Limpar a flag de sugestões para o próximo pedido, já que este foi enviado com sucesso
      setHasSeenSuggestions(false);
      
      if (merchantId) {
        trackProductsPurchased(merchantId, cartState.items);
      }
      
      setPendingOrderId(orderId);
      setProcessingProgress(20);
      setCurrentStep(5);
      
      // A observação agora é feita pelo useEffect dedicado
    } catch (error) {
      console.error('Error creating order:', error);
      const errorCode = getCheckoutErrorCode(error);
      toast.error(
        errorCode === CUSTOMER_BLOCKED_FOR_ORDERING
          ? CUSTOMER_BLOCKED_ORDERING_MESSAGE
          : 'Erro ao finalizar pedido. Tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const renderStepButton = () => {
    if (currentStep === 1) {
      return (
        <CheckoutButton 
          onClick={handleNextStep} 
          disabled={
            isBelowMinimum ||
            (!isRestaurantOpen && !isScheduling) ||
            (deliveryOption === 'delivery' && (!selectedAddress || isCalculatingFee)) || 
            (isScheduling && !scheduledDate)
          }
        >
          {isBelowMinimum ? 'Valor mínimo não atingido' : isCalculatingFee ? 'Calculando frete...' : 'Continuar'} <i className="fas fa-chevron-right" style={{ marginLeft: '8px' }}></i>
        </CheckoutButton>
      );
    }

    if (currentStep === 2) {
      return (
        <CheckoutButton onClick={handleNextStep}>
          Continuar <i className="fas fa-chevron-right" style={{ marginLeft: '8px' }}></i>
        </CheckoutButton>
      );
    }

    if (currentStep === 3) {
      const isPaymentSelected = isTwoPayments 
        ? (!!paymentMethod && !!paymentMethod2)
        : !!paymentMethod;
        
      return (
        <CheckoutButton 
          onClick={handleNextStep} 
          disabled={!isPaymentSelected || isCalculatingFee}
        >
          Continuar <i className="fas fa-chevron-right" style={{ marginLeft: '8px' }}></i>
        </CheckoutButton>
      );
    }

    if (currentStep === 4) {
      return (
        <CheckoutButton 
          onClick={handleSubmit} 
          disabled={isSubmitting || total <= 0 || isCalculatingFee}
        >
          {isSubmitting ? 'Processando...' : 'ENVIAR PEDIDO'}
        </CheckoutButton>
      );
    }

    return null;
  };

  return (
    <ModalOverlay zIndex={zIndex || 10000} onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()} maxWidth="500px" noPadding>
        <ModalHeader sticky noBorder className="flex-column">
          <div className="d-flex align-items-center w-100" style={{ minHeight: '40px' }}>
            <BackButton 
              onClick={currentStep === 1 || currentStep === 5 ? handleClose : handlePrevStep} 
              style={{ 
                position: 'absolute', 
                left: '1rem', 
                background: 'white', 
                borderRadius: '50%', 
                width: '36px', 
                height: '36px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                zIndex: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none'
              }}
            >
              <i className={currentStep === 1 || currentStep === 5 ? "fas fa-times" : "fas fa-arrow-left"} style={{ fontSize: '1.1rem', color: '#2d3748' }}></i>
            </BackButton>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <ModalTitle style={{ textAlign: 'center', fontSize: '1.2rem', margin: 0 }}>{currentStep === 5 ? 'Status do Pedido' : 'Checkout'}</ModalTitle>
            </div>
          </div>
          
          {/* Mobile Only Step Indicator */}
          <div className="d-flex d-sm-none w-100 justify-content-center">
            {currentStep < 5 && (
              <StepIndicator style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                <div style={{ position: 'absolute', top: 16, left: '1rem', right: '1rem', height: 2, zIndex: 1 }}>
                  <StepLine active={currentStep > 1} style={{ width: `${((currentStep - 1) / 3) * 100}%`, left: 0, right: 'auto' }} />
                </div>
                {[
                  { step: 1, label: 'Entrega', icon: 'fa-truck' },
                  { step: 2, label: 'Cashback', icon: 'fa-coins' },
                  { step: 3, label: 'Pagamento', icon: 'fa-credit-card' },
                  { step: 4, label: 'Confirmação', icon: 'fa-check-circle' }
                ].map((s) => (
                  <StepItem key={s.step} active={currentStep === s.step} completed={currentStep > s.step} style={{ width: '3.5rem' }}>
                    <StepCircle active={currentStep === s.step} completed={currentStep > s.step}>
                      {currentStep > s.step ? <i className="fas fa-check"></i> : s.step}
                    </StepCircle>
                    <StepLabel active={currentStep === s.step}>{s.label}</StepLabel>
                  </StepItem>
                ))}
              </StepIndicator>
            )}
          </div>
        </ModalHeader>
        
        <ModalBody padding="0 1.5rem 1.5rem 1.5rem">
          {/* Desktop Only Step Indicator at the top of ModalBody */}
          {currentStep < 5 && (
            <StepIndicatorWrapper className="d-none d-sm-flex">
              <StepIndicator style={{ margin: 0 }}>
                <div style={{ position: 'absolute', top: 16, left: '1rem', right: '1rem', height: 2, zIndex: 1 }}>
                  <StepLine active={currentStep > 1} style={{ width: `${((currentStep - 1) / 3) * 100}%`, left: 0, right: 'auto' }} />
                </div>
                {[
                  { step: 1, label: 'Entrega', icon: 'fa-truck' },
                  { step: 2, label: 'Cashback', icon: 'fa-coins' },
                  { step: 3, label: 'Pagamento', icon: 'fa-credit-card' },
                  { step: 4, label: 'Confirmação', icon: 'fa-check-circle' }
                ].map((s) => (
                  <StepItem key={s.step} active={currentStep === s.step} completed={currentStep > s.step} style={{ width: '3.5rem' }}>
                    <StepCircle active={currentStep === s.step} completed={currentStep > s.step}>
                      {currentStep > s.step ? <i className="fas fa-check"></i> : s.step}
                    </StepCircle>
                    <StepLabel active={currentStep === s.step}>{s.label}</StepLabel>
                  </StepItem>
                ))}
              </StepIndicator>
            </StepIndicatorWrapper>
          )}

          <WizardContainer>
            {currentStep === 1 && (
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Opção Entrega */}
                    {(deliveryOptions?.delivery !== false) && (
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', opacity: isRestaurantOpen || schedulingEnabled ? 1 : 0.6 }}>
                        <AddressSummary 
                          active={deliveryOption === 'delivery' && (isRestaurantOpen || schedulingEnabled)} 
                          clickable={isRestaurantOpen || schedulingEnabled}
                          onClick={() => (isRestaurantOpen || schedulingEnabled) && setDeliveryOption('delivery')}
                          style={{ border: 'none', marginBottom: 0, borderRadius: 0 }}
                        >
                          <i className="fas fa-bicycle"></i>
                          <AddressInfo>
                            <strong>Receber no seu endereço</strong>
                          </AddressInfo>
                          {deliveryOption === 'delivery' && (isRestaurantOpen || schedulingEnabled) ? <div className="check"><i className="fas fa-check"></i></div> : <div className="radio"></div>}
                        </AddressSummary>
                        {deliveryOption === 'delivery' && (isRestaurantOpen || schedulingEnabled) && (
                          <>
                            <AddressDetailRow onClick={() => setShowAddressModal(true)}>
                              <i className="fas fa-map-marker-alt"></i>
                              <span>
                                {selectedAddress ? `${selectedAddress.street}, ${selectedAddress.number}` : 'Clique aqui e informe o endereço'}
                              </span>
                              <div className="arrow">›</div>
                            </AddressDetailRow>
                            <AddressDetailRow style={{ borderTop: '1px solid #f1f5f9', cursor: 'default' }}>
                              <i className="fas fa-dollar-sign"></i>
                              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <span style={{ fontSize: '0.8rem', color: '#718096', fontWeight: 400 }}>Taxa de entrega</span>
                                <span style={{ color: (deliveryFee === 0 && !isCalculatingFee) ? '#27ae60' : '#2d3748', fontWeight: 700 }}>
                                  {isCalculatingFee ? 'Calculando...' : (deliveryFee > 0 ? formatCurrency(deliveryFee) : 'Grátis')}
                                </span>
                              </div>
                              {deliveryEstimatedTime && !isCalculatingFee && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <span style={{ fontSize: '0.8rem', color: '#718096', fontWeight: 400 }}>Previsão</span>
                                  <span style={{ color: '#2d3748', fontWeight: 700 }}>{deliveryEstimatedTime}</span>
                                </div>
                              )}
                            </AddressDetailRow>
                          </>
                        )}
                      </div>
                    )}

                    {/* Opção Retirada */}
                    {(deliveryOptions?.takeaway !== false) && (
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', opacity: isRestaurantOpen || schedulingEnabled ? 1 : 0.6 }}>
                        <AddressSummary 
                          active={deliveryOption === 'takeaway' && (isRestaurantOpen || schedulingEnabled)} 
                          clickable={isRestaurantOpen || schedulingEnabled}
                          onClick={() => (isRestaurantOpen || schedulingEnabled) && setDeliveryOption('takeaway')}
                          style={{ border: 'none', marginBottom: 0, borderRadius: 0 }}
                        >
                          <i className="fas fa-walking"></i>
                          <AddressInfo>
                            <strong>Retirar no estabelecimento</strong>
                          </AddressInfo>
                          {deliveryOption === 'takeaway' && (isRestaurantOpen || schedulingEnabled) ? <div className="check"><i className="fas fa-check"></i></div> : <div className="radio"></div>}
                        </AddressSummary>
                        {deliveryOption === 'takeaway' && (isRestaurantOpen || schedulingEnabled) && restaurant?.address && (
                          <AddressDetailRow style={{ cursor: 'default' }}>
                            <i className="fas fa-map-marker-alt"></i>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.8rem', color: '#718096', fontWeight: 400 }}>Endereço para retirada</span>
                              <span>{restaurant.address.street}, {restaurant.address.number}</span>
                              <span style={{ fontSize: '0.85rem', color: '#718096', fontWeight: 400 }}>{restaurant.address.neighborhood}, {restaurant.address.city} - {restaurant.address.state}</span>
                            </div>
                          </AddressDetailRow>
                        )}
                      </div>
                    )}

                    {/* Divisor pontilhado */}
                    {schedulingEnabled && (
                      <div style={{ borderTop: '1px dashed #e2e8f0', margin: '0.5rem 0' }}></div>
                    )}

                    {/* Lógica de Fechado e Agendamento */}
                    {(!isRestaurantOpen || schedulingEnabled) && (
                      <>
                        {!isRestaurantOpen && (
                          <ClosedMessage>Estamos fechados.{schedulingEnabled ? ' Agende seu pedido abaixo.' : ''}</ClosedMessage>
                        )}
                        {schedulingEnabled && (
                          <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                            <AddressSummary 
                              active={isScheduling} 
                              clickable
                              onClick={() => {
                                const newIsScheduling = !isScheduling;
                                setIsScheduling(newIsScheduling);
                                if (newIsScheduling && !scheduledDate) setIsSchedulingModalOpen(true);
                              }}
                              style={{ border: 'none', marginBottom: 0, borderRadius: 0 }}
                            >
                              <i className="far fa-calendar-alt"></i>
                              <AddressInfo>
                                <strong>Agendar pedido</strong>
                              </AddressInfo>
                              {isScheduling ? <div className="check"><i className="fas fa-check"></i></div> : <div className="radio"></div>}
                            </AddressSummary>
                            
                            {isScheduling && scheduledDate && scheduledTime && (
                              <AddressDetailRow onClick={() => setIsSchedulingModalOpen(true)}>
                                <i className="far fa-calendar-check"></i>
                                <ScheduledInfo>
                                  <strong>Agendado para {scheduledDate.charAt(0).toUpperCase() + scheduledDate.slice(1)}</strong>
                                  <span>às {scheduledTime}</span>
                                </ScheduledInfo>
                                <EditButton onClick={(e) => { e.stopPropagation(); setIsSchedulingModalOpen(true); }}>Editar</EditButton>
                              </AddressDetailRow>
                            )}
                          </div>
                        )}
                        {isBelowMinimum && (
                          <div style={{ padding: '0.8rem', backgroundColor: restaurant?.theme?.primary ? `${restaurant?.theme?.primary}1A` : '#fff5f5', borderRadius: '12px', border: `1px solid ${restaurant?.theme?.primary ? `${restaurant?.theme?.primary}33` : '#feb2b2'}`, display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '0.5rem' }}>
                            <i className="fas fa-exclamation-circle" style={{ color: restaurant?.theme?.primary || '#e53e3e' }}></i>
                            <span style={{ fontSize: '0.9rem', color: restaurant?.theme?.primary || '#c53030', fontWeight: 600 }}>
                              O valor mínimo do pedido é {formatCurrency(minimumOrderValue)}. Adicione mais itens para continuar.
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  {!isAuthenticated ? (
                    <LoginPrompt onClick={() => setShowLoginModal(true)}>
                      <i className="fas fa-exclamation-circle"></i>
                      <p>
                        Faça login para ver e utilizar seu saldo de <strong>Cashback</strong>!
                      </p>
                    </LoginPrompt>
                  ) : (
                    <>
                      <SectionTitle style={{ color: '#2d3748', marginBottom: '1rem' }}>Gerenciar Cashback</SectionTitle>
                      
                      <CashbackSection>
                        <CashbackHeader>
                          <div className="title">
                            <i className="fas fa-wallet"></i>
                            <strong>Seu Saldo</strong>
                          </div>
                          <div className="balance">
                            {formatCurrency(user?.availableCashbackBalance || 0)}
                          </div>
                        </CashbackHeader>

                        {cartState.appliedCashback > 0 ? (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', padding: '0.75rem', borderRadius: '8px', border: '1px solid #276749' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.8rem', color: '#718096' }}>Valor aplicado</span>
                              <span style={{ fontWeight: 700, color: '#276749' }}>{formatCurrency(cartState.appliedCashback)}</span>
                            </div>
                            <CashbackButton onClick={removeCashback} style={{ margin: 0 }}>REMOVER</CashbackButton>
                          </div>
                        ) : (
                          <>
                            <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#4a5568' }}>
                              Você pode usar até <strong>{formatCurrency(maxAllowedCashback)}</strong> neste pedido.
                            </div>
                            <CashbackInputWrapper>
                              <CurrencyInput 
                                value={cashbackInputValue}
                                onChange={(val) => setCashbackInputValue(val)}
                                disabled={maxAllowedCashback <= 0}
                              />
                              <button 
                                onClick={() => {
                                  const amount = cashbackInputValue;
                                  if (amount > 0 && amount <= maxAllowedCashback) {
                                    applyCashback(amount);
                                    setCashbackInputValue(0);
                                  } else if (amount > maxAllowedCashback) {
                                    toast.error(`O valor máximo permitido é ${formatCurrency(maxAllowedCashback)}`);
                                  } else {
                                    toast.error('Valor inválido');
                                  }
                                }}
                                disabled={maxAllowedCashback <= 0 || cashbackInputValue <= 0}
                              >
                                APLICAR
                              </button>
                            </CashbackInputWrapper>
                            {maxAllowedCashback > 0 && (
                              <button 
                                onClick={() => setCashbackInputValue(maxAllowedCashback)}
                                style={{ 
                                  marginTop: '0.5rem', 
                                  width: '100%', 
                                  padding: '0.5rem', 
                                  borderRadius: '8px', 
                                  border: `1px dashed ${restaurant?.theme?.primary || '#3182ce'}`,
                                  background: 'transparent',
                                  color: restaurant?.theme?.primary || '#3182ce',
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                <i className="fas fa-magic" style={{ marginRight: '0.5rem' }}></i>
                                USAR VALOR MÁXIMO PERMITIDO ({formatCurrency(maxAllowedCashback)})
                              </button>
                            )}
                          </>
                        )}
                        
                        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#718096' }}>
                          O cashback será descontado do total do seu pedido.
                        </div>
                      </CashbackSection>

                      <CashbackBadge>
                        <i className="fas fa-coins"></i>
                        <span>
                          Nesse pedido você ganhará aproximadamente <strong>{formatCurrency(cashbackEarned)}</strong> de cashback
                        </span>
                      </CashbackBadge>


                      <CashbackSectionNote style={{ marginTop: '1.5rem' }}>
                        O cashback pendente ficará disponível após a conclusão do pedido. Cashback expirado não pode ser recuperado.
                      </CashbackSectionNote>
                    </>
                  )}
                </div>
              )}

              {currentStep === 3 && (
                <div>
                  <CouponCard onClick={() => !cartState.coupon && setIsCouponModalOpen(true)}>
                    <i className="fas fa-ticket-alt"></i>
                    <CouponCardContent>
                      <strong>Tem um cupom?</strong>
                      <span>{cartState.coupon ? `Cupom ${cartState.coupon.code} aplicado` : 'Clique e insira o código'}</span>
                    </CouponCardContent>
                    {cartState.coupon ? (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCoupon();
                        }}
                        style={{ 
                          border: 'none', 
                          background: 'none', 
                          color: '#e74c3c', 
                          fontSize: '0.85rem',
                          textDecoration: 'underline',
                          fontWeight: 'bold'
                        }}
                      >
                        Remover
                      </button>
                    ) : (
                      <i className="fas fa-chevron-right" style={{ fontSize: '1rem' }}></i>
                    )}
                  </CouponCard>

                  {isTwoPayments ? (
                    <div>
                      <SectionTitle>Escolha a 1ª forma de pagamento</SectionTitle>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {paymentMethod ? (
                          <PaymentOption selected>
                            {renderPaymentMethodIcon(paymentMethod)}
                            <PaymentOptionInfo>
                              <span>{getCheckoutPaymentMethodLabel(paymentMethod)}</span>
                              {paymentMethod && PAYMENT_METHOD_DESCRIPTIONS[paymentMethod] && (
                                <span style={{ fontSize: '0.75rem', color: '#718096' }}>
                                  {PAYMENT_METHOD_DESCRIPTIONS[paymentMethod]}
                                </span>
                              )}
                              <span style={{ fontSize: '0.85rem', color: '#718096' }}>
                                {formatCurrency(paymentAmount1)}
                              </span>
                            </PaymentOptionInfo>
                            <div className="check"><i className="fas fa-check"></i></div>
                            
                            {paymentMethod === PaymentMethod.CASH && needsChange !== null && (
                              <ChangeInfoSection>
                                <ChangeText>
                                  {needsChange && Number(changeFor) >= paymentAmount1 
                                    ? `Troco para ${formatCurrency(Number(changeFor))}` 
                                    : 'Não precisa de troco'}
                                </ChangeText>
                                <EditChangeButton onClick={(e) => {
                                  e.stopPropagation();
                                  setIsChangeModalOpen(true);
                                }}>
                                  EDITAR TROCO
                                </EditChangeButton>
                              </ChangeInfoSection>
                            )}

                            <PaymentActions>
                              <PaymentActionButton onClick={(e) => {
                                e.stopPropagation();
                                setCurrentSelectingPayment(1);
                                setIsEditingAmount(false);
                                setShowTwoPaymentsSelection(true);
                              }}>
                                <i className="fas fa-exchange-alt"></i> MUDAR MÉTODO
                              </PaymentActionButton>
                              <PaymentActionButton onClick={(e) => {
                                e.stopPropagation();
                                setCurrentSelectingPayment(1);
                                setIsEditingAmount(true);
                                setShowTwoPaymentsSelection(true);
                              }}>
                                <i className="fas fa-edit"></i> EDITAR VALOR
                              </PaymentActionButton>
                            </PaymentActions>
                          </PaymentOption>
                        ) : (
                          availablePaymentMethods.map(method => (
                            <PaymentOption 
                              key={method.id} 
                              onClick={() => {
                                setPaymentMethod(method.id);
                                setCurrentSelectingPayment(1);
                                setIsEditingAmount(false);
                                setShowTwoPaymentsSelection(true);
                                if (method.id === PaymentMethod.CASH) {
                                  setIsChangeModalOpen(true);
                                }
                              }}
                            >
                              {renderPaymentMethodIcon(method.id)}
                              <PaymentOptionInfo>
                                <span>{method.name}</span>
                                {PAYMENT_METHOD_DESCRIPTIONS[method.id] && (
                                  <span style={{ fontSize: '0.75rem', color: '#718096' }}>
                                    {PAYMENT_METHOD_DESCRIPTIONS[method.id]}
                                  </span>
                                )}
                              </PaymentOptionInfo>
                            </PaymentOption>
                          ))
                        )}
                      </div>

                      <SectionTitle style={{ marginTop: '1.5rem' }}>Escolha a 2ª forma de pagamento</SectionTitle>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {paymentMethod2 ? (
                          <PaymentOption selected>
                            {renderPaymentMethodIcon(paymentMethod2)}
                            <PaymentOptionInfo>
                              <span>{getCheckoutPaymentMethodLabel(paymentMethod2)}</span>
                              {paymentMethod2 && PAYMENT_METHOD_DESCRIPTIONS[paymentMethod2] && (
                                <span style={{ fontSize: '0.75rem', color: '#718096' }}>
                                  {PAYMENT_METHOD_DESCRIPTIONS[paymentMethod2]}
                                </span>
                              )}
                              <span style={{ fontSize: '0.85rem', color: '#718096' }}>
                                {formatCurrency(paymentAmount2)}
                              </span>
                            </PaymentOptionInfo>
                            <div className="check"><i className="fas fa-check"></i></div>

                            {paymentMethod2 === PaymentMethod.CASH && needsChange2 !== null && (
                              <ChangeInfoSection>
                                <ChangeText>
                                  {needsChange2 && Number(changeFor2) > paymentAmount2 
                                    ? `Troco para ${formatCurrency(Number(changeFor2))}` 
                                    : 'Não precisa de troco'}
                                </ChangeText>
                                <EditChangeButton onClick={(e) => {
                                  e.stopPropagation();
                                  setIsChangeModalOpen2(true);
                                }}>
                                  EDITAR TROCO
                                </EditChangeButton>
                              </ChangeInfoSection>
                            )}

                            <PaymentActions>
                              <PaymentActionButton onClick={(e) => {
                                e.stopPropagation();
                                setCurrentSelectingPayment(2);
                                setIsEditingAmount(false);
                                setShowTwoPaymentsSelection(true);
                              }}>
                                <i className="fas fa-exchange-alt"></i> MUDAR MÉTODO
                              </PaymentActionButton>
                              <PaymentActionButton onClick={(e) => {
                                e.stopPropagation();
                                setCurrentSelectingPayment(2);
                                setIsEditingAmount(true);
                                setShowTwoPaymentsSelection(true);
                              }}>
                                <i className="fas fa-edit"></i> EDITAR VALOR
                              </PaymentActionButton>
                            </PaymentActions>
                          </PaymentOption>
                        ) : (
                          availablePaymentMethods.map(method => (
                            <PaymentOption 
                              key={method.id} 
                              onClick={() => {
                                setPaymentMethod2(method.id);
                                setCurrentSelectingPayment(2);
                                setIsEditingAmount(false);
                                setShowTwoPaymentsSelection(true);
                                if (method.id === PaymentMethod.CASH) {
                                  setIsChangeModalOpen2(true);
                                }
                              }}
                            >
                              {renderPaymentMethodIcon(method.id)}
                              <PaymentOptionInfo>
                                <span>{method.name}</span>
                                {PAYMENT_METHOD_DESCRIPTIONS[method.id] && (
                                  <span style={{ fontSize: '0.75rem', color: '#718096' }}>
                                    {PAYMENT_METHOD_DESCRIPTIONS[method.id]}
                                  </span>
                                )}
                              </PaymentOptionInfo>
                            </PaymentOption>
                          ))
                        )}
                      </div>

                      <TwoPaymentsButton onClick={() => {
                        setIsTwoPayments(false);
                        setPaymentMethod(defaultPaymentMethod);
                      }} style={{ color: '#718096' }}>
                        Pagar com apenas uma forma
                      </TwoPaymentsButton>
                    </div>
                  ) : (
                    <>
                      <SectionTitle>Pagar na entrega</SectionTitle>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {availablePaymentMethods.map(method => {
                          const isSelected = paymentMethod === method.id;
                          return (
                            <PaymentOption 
                              key={method.id} 
                              selected={isSelected}
                              onClick={() => {
                                setPaymentMethod(method.id);
                                if (method.id === PaymentMethod.CASH) {
                                  setIsChangeModalOpen(true);
                                }
                              }}
                            >
                              {renderPaymentMethodIcon(method.id)}
                              <PaymentOptionInfo>
                                <span>{method.name}</span>
                                {PAYMENT_METHOD_DESCRIPTIONS[method.id] && (
                                  <span style={{ fontSize: '0.75rem', color: '#718096' }}>
                                    {PAYMENT_METHOD_DESCRIPTIONS[method.id]}
                                  </span>
                                )}
                              </PaymentOptionInfo>
                              {isSelected && <div className="check"><i className="fas fa-check"></i></div>}
                              
                              {isSelected && method.id === PaymentMethod.CASH && needsChange !== null && (
                                <ChangeInfoSection>
                                  <ChangeText>
                                    {needsChange && Number(changeFor) > total 
                                      ? `Troco para ${formatCurrency(Number(changeFor))}` 
                                      : 'Não precisa de troco'}
                                  </ChangeText>
                                  <EditChangeButton onClick={(e) => {
                                    e.stopPropagation();
                                    setIsChangeModalOpen(true);
                                  }}>
                                    EDITAR TROCO
                                  </EditChangeButton>
                                </ChangeInfoSection>
                              )}
                            </PaymentOption>
                          );
                        })}
                      </div>


                      <TwoPaymentsButton onClick={() => {
                         setIsTwoPayments(true);
                         setPaymentMethod(null);
                         setPaymentMethod2(null);
                         setPaymentAmount1(total / 2);
                         setPaymentAmount2(total / 2);
                         setCurrentSelectingPayment(1);
                         setIsEditingAmount(false);
                         setShowTwoPaymentsSelection(true);
                      }}>
                        Pagar com duas formas de pagamento
                      </TwoPaymentsButton>
                    </>
                  )}

                  <SummarySection>
                    <SummaryRow>
                      <span>Subtotal</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </SummaryRow>

                    {deliveryOption === 'delivery' && (
                      <SummaryRow>
                        <span>Taxa de entrega</span>
                        <span>{isCalculatingFee ? 'Calculando...' : (deliveryFee > 0 ? formatCurrency(deliveryFee) : 'Grátis')}</span>
                      </SummaryRow>
                    )}

                    {cartState.appliedCashback > 0 && (
                      <SummaryRow>
                        <span>Cashback</span>
                        <span style={{ color: '#38a169' }}>-{formatCurrency(cartState.appliedCashback)}</span>
                      </SummaryRow>
                    )}

                    {cartState.coupon && (
                      <SummaryRow>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>Cupom</span>
                          <small style={{ color: '#718096', fontSize: '0.75rem' }}>{cartState.coupon.code}</small>
                        </div>
                        <span style={{ color: '#38a169' }}>
                          -{formatCurrency(cartState.coupon.discountAmount !== undefined ? cartState.coupon.discountAmount : 
                            (cartState.coupon.discountValue || (cartState.coupon.discountPercentage ? (subtotal * cartState.coupon.discountPercentage / 100) : 0)))}
                        </span>
                      </SummaryRow>
                    )}

                    {cashbackEarned > 0 && (
                      <SummaryRow>
                        <span>Cashback a ganhar</span>
                        <span style={{ color: '#3182ce', fontWeight: 'bold' }}>+{formatCurrency(cashbackEarned)}</span>
                      </SummaryRow>
                    )}

                    <SummaryRow total>
                      <span>Total</span>
                      <span>{isCalculatingFee ? '...' : formatCurrency(total)}</span>
                    </SummaryRow>
                  </SummarySection>
                </div>
              )}

              {currentStep === 4 && (
                <div>
                  {isScheduling && (
                    <ConfirmationSection style={{ textAlign: 'center' }}>
                      <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Pedido agendado para</p>
                      <strong style={{ fontSize: '1.2rem', color: '#2d3748', display: 'block' }}>{scheduledDate}</strong>
                      <strong style={{ fontSize: '1.2rem', color: '#2d3748', display: 'block' }}>{scheduledTime}</strong>
                    </ConfirmationSection>
                  )}

                  <ConfirmationSection>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <SectionTitle style={{ margin: 0 }}>
                        {deliveryOption === 'takeaway' ? 'Retirar no estabelecimento' : 'Entrega'}
                      </SectionTitle>
                      <EditButton onClick={() => setCurrentStep(1)}>
                        <i className="fas fa-edit"></i>
                      </EditButton>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <i className="fas fa-user" style={{ color: '#a0aec0', marginTop: '4px', width: '20px', textAlign: 'center' }}></i>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong style={{ color: '#2d3748' }}>{user?.name || 'Cliente'}</strong>
                        <span style={{ color: '#718096', fontSize: '0.9rem' }}>{user?.phone ? formatPhone(user.phone) : ''}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      <i className={`fas ${deliveryOption === 'takeaway' ? 'fa-walking' : 'fa-map-marker-alt'}`} style={{ color: '#a0aec0', marginTop: '4px', width: '20px', textAlign: 'center' }}></i>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {deliveryOption === 'takeaway' ? (
                          <>
                            <strong style={{ color: '#2d3748' }}>{restaurant?.address?.street || 'Endereço do restaurante'}</strong>
                            <span style={{ color: '#718096', fontSize: '0.9rem' }}>{restaurant?.address?.city || ''}</span>
                          </>
                        ) : (
                          <>
                            <strong style={{ color: '#2d3748' }}>
                              {selectedAddress?.street}{selectedAddress?.number ? `, ${selectedAddress.number}` : ''}
                            </strong>
                            <span style={{ color: '#718096', fontSize: '0.9rem' }}>
                              {selectedAddress?.neighborhood}{selectedAddress?.city ? `, ${selectedAddress.city}` : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </ConfirmationSection>

                  <ConfirmationSection>
                    {cartState.items.map((item, index) => (
                      <OrderItemRow key={index}>
                        <div className="qty">{item.quantity}x</div>
                        <div className="details">
                          <strong>{item.name}</strong>
                          {item.options && item.options.length > 0 && (
                            <span>{item.options.map(opt => opt.name).join(', ')}</span>
                          )}
                        </div>
                        <div className="price">{formatCurrency(item.price * item.quantity)}</div>
                      </OrderItemRow>
                    ))}
                  </ConfirmationSection>

                  <SummarySection>
                    <SummaryRow>
                      <span>Subtotal</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </SummaryRow>
                    {deliveryOption === 'delivery' && (
                      <SummaryRow>
                        <span>Taxa de entrega</span>
                        <span>{isCalculatingFee ? 'Calculando...' : (deliveryFee > 0 ? formatCurrency(deliveryFee) : 'Grátis')}</span>
                      </SummaryRow>
                    )}

                    {cartState.appliedCashback > 0 && (
                      <SummaryRow>
                        <span>Cashback</span>
                        <span style={{ color: '#38a169' }}>-{formatCurrency(cartState.appliedCashback)}</span>
                      </SummaryRow>
                    )}

                    {cartState.coupon && (
                      <SummaryRow>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>Cupom</span>
                          <small style={{ color: '#718096', fontSize: '0.75rem' }}>{cartState.coupon.code}</small>
                        </div>
                        <span style={{ color: '#38a169' }}>
                          -{formatCurrency(cartState.coupon.discountAmount !== undefined ? cartState.coupon.discountAmount : 
                            (cartState.coupon.discountValue || (cartState.coupon.discountPercentage ? (subtotal * cartState.coupon.discountPercentage / 100) : 0)))}
                        </span>
                      </SummaryRow>
                    )}
                    {cashbackEarned > 0 && (
                      <SummaryRow>
                        <span>Cashback a ganhar</span>
                        <span style={{ color: '#3182ce', fontWeight: 'bold' }}>+{formatCurrency(cashbackEarned)}</span>
                      </SummaryRow>
                    )}
                    <SummaryRow total>
                      <span>Total</span>
                      <span>{isCalculatingFee ? '...' : formatCurrency(total)}</span>
                    </SummaryRow>
                  </SummarySection>

                  <ConfirmationSection>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <SectionTitle style={{ margin: 0 }}>Pagamento</SectionTitle>
                      <EditButton onClick={() => setCurrentStep(3)}>
                        <i className="fas fa-edit"></i>
                      </EditButton>
                    </div>

                    <PaymentSummaryRow>
                      {renderPaymentMethodIcon(paymentMethod)}
                      <div className="info">
                        <strong>
                          {getCheckoutPaymentMethodLabel(paymentMethod)}
                          {isTwoPayments ? ` (${formatCurrency(paymentAmount1)})` : ` (${formatCurrency(total)})`}
                        </strong>
                        {paymentMethod === PaymentMethod.CASH && (needsChange === true || (needsChange && Number(changeFor) > (isTwoPayments ? paymentAmount1 : total))) && (
                          <span>Troco para {formatCurrency(Number(changeFor))}</span>
                        )}
                      </div>
                    </PaymentSummaryRow>

                    {isTwoPayments && paymentMethod2 && (
                      <PaymentSummaryRow>
                        {renderPaymentMethodIcon(paymentMethod2)}
                        <div className="info">
                          <strong>
                            {getCheckoutPaymentMethodLabel(paymentMethod2)}
                            {` (${formatCurrency(paymentAmount2)})`}
                          </strong>
                          {paymentMethod2 === PaymentMethod.CASH && (needsChange2 === true || (needsChange2 && Number(changeFor2) > paymentAmount2)) && (
                            <span>Troco para {formatCurrency(Number(changeFor2))}</span>
                          )}
                        </div>
                      </PaymentSummaryRow>
                    )}
                  </ConfirmationSection>

                  <SectionTitle style={{ marginTop: '1.5rem' }}>Observações</SectionTitle>
                  <InputGroup style={{ marginBottom: '1.5rem' }}>
                    <TextArea 
                      placeholder="Ex: sem cebola, retirar no portão, etc." 
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                    />
                  </InputGroup>
                </div>
              )}

              {currentStep === 5 && !lastOrder && (
                <StatusContainer style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                  <OrderProcessingAnimation 
                    progress={processingProgress} 
                    primaryColor={restaurant?.theme?.primary || '#e74c3c'} 
                  />
                </StatusContainer>
              )}

              {currentStep === 5 && lastOrder && (
                <StatusContainer>
                  {isOrderCancelled(lastOrder.status) ? (
                    <StatusHeader>
                      <StatusBadgeContainer status={lastOrder.status}>
                        <i className="fas fa-times-circle"></i>
                        Pedido #{lastOrder.orderDisplayId || lastOrder.id.substring(0, 8).toUpperCase()}
                      </StatusBadgeContainer>
                      <StatusTitle>Pedido Cancelado</StatusTitle>
                      <StatusSubtitle style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                        {lastOrder.cancellationReasonDescription || 'Lamentamos, mas seu pedido foi cancelado.'}
                      </StatusSubtitle>
                      {lastOrder.cancellationReason && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                          <strong>Motivo:</strong> {lastOrder.cancellationReason}
                        </div>
                      )}
                      <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
                        <CheckoutButton 
                          onClick={() => { setLastOrder(null); setCurrentStep(1); }}
                          style={{ width: 'auto', padding: '0.8rem 2rem' }}
                        >
                          Refazer Pedido
                        </CheckoutButton>
                      </div>
                    </StatusHeader>
                  ) : (
                    <>
                      <OrderHeaderCard>
                        <OrderIdBox>
                          <span>Pedido</span>
                          <OrderCodeBadge accepted={[
                            OrderStatus.ACCEPTED,
                            OrderStatus.READY_FOR_DELIVERY,
                            OrderStatus.READY_FOR_TAKEAWAY,
                            OrderStatus.OUT_FOR_DELIVERY,
                            OrderStatus.DELIVERED
                          ].includes(lastOrder.status as OrderStatus)}>
                            #{lastOrder.orderDisplayId || lastOrder.id.substring(0, 8).toUpperCase()}
                          </OrderCodeBadge>
                          <small>{formatDateTimeShort(lastOrder.createdAt)}</small>
                        </OrderIdBox>

                        {(lastOrder.estimatedDeliveryTime || deliveryEstimatedTime) && (
                          <DeliveryEstimateBox accepted={[
                            OrderStatus.ACCEPTED,
                            OrderStatus.READY_FOR_TAKEAWAY,
                            OrderStatus.OUT_FOR_DELIVERY,
                            OrderStatus.DELIVERED
                          ].includes(lastOrder.status as OrderStatus)}>
                            <strong>Entrega</strong>
                            <span>{formatMaybeIso(lastOrder.estimatedDeliveryTime || deliveryEstimatedTime || '')}</span>
                          </DeliveryEstimateBox>
                        )}
                      </OrderHeaderCard>

                      {lastOrder.deliveryConfirmationCode && 
                       ![OrderStatus.RECEIVED, OrderStatus.DELIVERED].includes(lastOrder.status as OrderStatus) && 
                       !isOrderCancelled(lastOrder.status) && (
                        <ConfirmationCodeContainer>
                          <ConfirmationCodeTitle>Código de Confirmação</ConfirmationCodeTitle>
                          <ConfirmationCodeValue>{lastOrder.deliveryConfirmationCode}</ConfirmationCodeValue>
                          <ConfirmationCodeLabel>
                            Informe este código ao entregador para confirmar que você recebeu seu pedido.
                          </ConfirmationCodeLabel>
                        </ConfirmationCodeContainer>
                      )}

                      <StatusTimelineContainer>
                        <VerticalTimeline 
                          progressHeight="100%"
                        />
                        <TimelineSteps>
                          {[
                            { 
                              status: OrderStatus.RECEIVED, 
                              label: 'Pedido realizado', 
                              time: formatDateTimeShort(lastOrder.createdAt) 
                            },
                            { 
                              status: OrderStatus.ACCEPTED, 
                              label: 'Pedido foi aceito e está sendo preparado', 
                              time: [OrderStatus.ACCEPTED, OrderStatus.READY_FOR_DELIVERY, OrderStatus.READY_FOR_TAKEAWAY, OrderStatus.OUT_FOR_DELIVERY, OrderStatus.DELIVERED].includes(lastOrder.status as OrderStatus) ? 
                                formatDateTimeShort(lastOrder.updatedAt || lastOrder.createdAt) : 
                                null
                            },
                            { 
                              status: OrderStatus.OUT_FOR_DELIVERY, 
                              label: 'Pedido saiu para entrega', 
                              time: [OrderStatus.OUT_FOR_DELIVERY, OrderStatus.DELIVERED].includes(lastOrder.status as OrderStatus) ? 
                                formatDateTimeShort(lastOrder.updatedAt || lastOrder.createdAt) : 
                                null
                            },
                            { 
                              status: OrderStatus.DELIVERED, 
                              label: 'Pedido foi concluído', 
                              time: lastOrder.status === OrderStatus.DELIVERED ? 
                                formatDateTimeShort(lastOrder.updatedAt || lastOrder.createdAt) : 
                                null
                            }
                          ].filter(step => step.time !== null).map((step, idx, arr) => {
                            const isActive = idx === arr.length - 1;

                            return (
                              <TimelineStepItem key={step.status}>
                                <StepPoint active={isActive} completed={true} />
                                <StepCard active={isActive}>
                                  <StepTime>{step.time}</StepTime>
                                  <StepDesc>{step.label}</StepDesc>
                                </StepCard>
                              </TimelineStepItem>
                            );
                          })}
                        </TimelineSteps>
                      </StatusTimelineContainer>

                      {canReviewOrder(lastOrder) && (
                        <CheckoutButton
                          style={{
                            marginTop: '1rem',
                            backgroundColor: '#f59e0b',
                            border: 'none',
                            boxShadow: 'none',
                            width: '100%'
                          }}
                          onClick={() => openOrderReview(lastOrder)}
                        >
                          AVALIAR PEDIDO
                        </CheckoutButton>
                      )}
                    </>
                  )}

                  <OrderDetailCard style={{ marginTop: '2rem' }}>
                    <DetailSection>
                      <DetailTitle>
                        <i className="fas fa-map-marker-alt"></i>
                        {lastOrder.deliveryMethod === DeliveryMethod.TAKEAWAY ? 'Local de Retirada' : 'Endereço de Entrega'}
                      </DetailTitle>
                      <DetailContent>
                        {lastOrder.deliveryMethod === DeliveryMethod.TAKEAWAY ? (
                          <>
                            <strong>{restaurant?.name}</strong><br />
                            <span style={{ fontSize: '0.85rem' }}>
                              {restaurant?.address?.street}, {restaurant?.address?.number}<br />
                              {restaurant?.address?.neighborhood}, {restaurant?.address?.city}
                            </span>
                          </>
                        ) : (
                          <>
                            <strong>{lastOrder.deliveryAddress?.street}, {lastOrder.deliveryAddress?.number}</strong><br />
                            <span style={{ fontSize: '0.85rem' }}>
                              {lastOrder.deliveryAddress?.neighborhood}, {lastOrder.deliveryAddress?.city}
                            </span>
                          </>
                        )}
                      </DetailContent>
                    </DetailSection>

                    <DetailSection>
                      <DetailTitle>
                        <i className="fas fa-credit-card"></i>
                        Pagamento
                      </DetailTitle>
                      <DetailContent>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {lastOrder.payments && lastOrder.payments.length > 0 ? (
                            <>
                              {lastOrder.payments.map((p, idx) => (
                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.85rem' }}>
                                    {getCheckoutPaymentMethodLabel(p.method)}
                                  </span>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    {formatCurrency(p.amount)}
                                  </span>
                                </div>
                              ))}
                              <div style={{ borderTop: '1px solid #edf2f7', marginTop: '4px', paddingTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>Total Pago</span>
                                <strong style={{ fontSize: '1.1rem', color: '#2d3748' }}>
                                  {formatCurrency(lastOrder.payments.reduce((sum, p) => sum + p.amount, 0))}
                                </strong>
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.85rem' }}>
                                {lastOrder.paymentMethod ? getCheckoutPaymentMethodLabel(lastOrder.paymentMethod) : 'Não informado'}
                              </span>
                              <strong style={{ fontSize: '1.1rem', color: '#2d3748' }}>{formatCurrency(lastOrder.total)}</strong>
                            </div>
                          )}
                        </div>
                      </DetailContent>
                    </DetailSection>

                    <DetailSection fullWidth>
                      <DetailTitle>
                        <i className="fas fa-list"></i>
                        Resumo do Pedido
                      </DetailTitle>
                      <DetailContent>
                        <div style={{ maxHeight: '120px', overflowY: 'auto', paddingRight: '5px' }}>
                          {lastOrder.items.map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
                              <span>{item.quantity}x {item.name}</span>
                              <span style={{ fontWeight: 600 }}>{formatCurrency(item.price * item.quantity)}</span>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: '0.5rem', borderTop: '1px dashed #e2e8f0', paddingTop: '0.5rem' }}>
                          {lastOrder.subtotal !== undefined && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#718096', marginBottom: '2px' }}>
                              <span>Subtotal</span>
                              <span>{formatCurrency(lastOrder.subtotal)}</span>
                            </div>
                          )}
                          {lastOrder.deliveryFee !== undefined && lastOrder.deliveryFee > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#718096', marginBottom: '2px' }}>
                              <span>Taxa de Entrega</span>
                              <span>{formatCurrency(lastOrder.deliveryFee)}</span>
                            </div>
                          )}
                          {lastOrder.discount !== undefined && lastOrder.discount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#e53e3e', marginBottom: '2px' }}>
                              <span>Desconto {lastOrder.couponCode ? `(${lastOrder.couponCode})` : ''}</span>
                              <span>-{formatCurrency(lastOrder.discount)}</span>
                            </div>
                          )}
                          {lastOrder.cashbackUsedAmount !== undefined && lastOrder.cashbackUsedAmount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#38a169', marginBottom: '2px' }}>
                              <span>Cashback Utilizado</span>
                              <span>-{formatCurrency(lastOrder.cashbackUsedAmount)}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 700, marginTop: '4px' }}>
                            <span>Total do Pedido</span>
                            <span>{formatCurrency(lastOrder.total)}</span>
                          </div>
                        </div>
                      </DetailContent>
                    </DetailSection>
                  </OrderDetailCard>

                  <StatusHelpSection>
                    <StatusHelpTitle>Precisa de ajuda com o pedido?</StatusHelpTitle>
                    <StatusHelpButton onClick={() => window.open(`https://wa.me/${restaurant?.contact?.phone?.replace(/\D/g, '')}`, '_blank')}>
                      <i className="fab fa-whatsapp"></i>
                      Falar com o estabelecimento
                    </StatusHelpButton>
                  </StatusHelpSection>

                  <CheckoutButton 
                    style={{ 
                      marginTop: '1.5rem', 
                      backgroundColor: 'transparent', 
                      color: '#718096', 
                      border: '1px solid #e2e8f0', 
                      boxShadow: 'none',
                      fontSize: '0.9rem',
                      padding: '0.75rem',
                      width: '100%'
                    }}
                    onClick={handleClose}
                  >
                    VOLTAR PARA O CARDÁPIO
                  </CheckoutButton>
                </StatusContainer>
              )}
            </WizardContainer>
        </ModalBody>

        {currentStep < 5 && (
          <ModalFooter mobileFlexRow={true}>
            {renderStepButton()}
          </ModalFooter>
        )}

        {isWhatsAppPromptOpen && (
          <ModalOverlay zIndex={10150} onClick={() => setIsWhatsAppPromptOpen(false)}>
            <ModalContent onClick={e => e.stopPropagation()} maxWidth="420px">
              <ModalHeader noBorder>
                <BackButton 
                  onClick={() => setIsWhatsAppPromptOpen(false)} 
                  style={{ 
                    position: 'absolute', 
                    left: '1rem', 
                    background: 'white', 
                    borderRadius: '50%', 
                    width: '32px', 
                    height: '32px', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                    zIndex: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '1rem', color: '#2d3748' }}></i>
                </BackButton>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <ModalTitle style={{ fontSize: '1.15rem' }}>Agilize seu atendimento</ModalTitle>
                </div>
              </ModalHeader>
              <ModalBody>
                <WhatsAppPromptContent>
                  <WhatsAppPromptIcon>
                    <i className="fab fa-whatsapp"></i>
                  </WhatsAppPromptIcon>
                  <WhatsAppPromptTitle>Enviar dados do pedido pelo WhatsApp?</WhatsAppPromptTitle>
                  <WhatsAppPromptText>
                    Compartilhe o resumo do pedido com o estabelecimento para facilitar a conferência e acelerar o atendimento.
                  </WhatsAppPromptText>
                  <WhatsAppPromptActions>
                    <WhatsAppPromptButton type="button" onClick={handleOpenOrderWhatsApp}>
                      <i className="fab fa-whatsapp"></i>
                      Enviar pedido
                    </WhatsAppPromptButton>
                    <button type="button" onClick={() => setIsWhatsAppPromptOpen(false)}>
                      Agora não
                    </button>
                  </WhatsAppPromptActions>
                </WhatsAppPromptContent>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {isSchedulingModalOpen && (
          <ModalOverlay zIndex={10100} onClick={() => setIsSchedulingModalOpen(false)}>
            <ModalContent onClick={e => e.stopPropagation()} maxWidth="400px">
              <ModalHeader noBorder>
                <BackButton 
                  onClick={() => setIsSchedulingModalOpen(false)} 
                  style={{ 
                    position: 'absolute', 
                    left: '1rem', 
                    background: 'white', 
                    borderRadius: '50%', 
                    width: '32px', 
                    height: '32px', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                    zIndex: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '1rem', color: '#2d3748' }}></i>
                </BackButton>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <ModalTitle style={{ fontSize: '1.2rem' }}>Agendar pedido</ModalTitle>
                </div>
              </ModalHeader>
              <ModalBody>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <InputGroup>
                    <Label>Data</Label>
                    <Select 
                      value={scheduledDate || ''} 
                      onChange={(e) => setScheduledDate(e.target.value)}
                    >
                      <option value="">Selecione uma data</option>
                      {/* Exemplo de datas */}
                      {Array.from({ length: 7 }).map((_, i) => {
                        const date = new Date();
                        date.setDate(date.getDate() + i);
                        const dateStr = date.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
                        const valueStr = date.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
                        return <option key={i} value={valueStr}>{dateStr.charAt(0).toUpperCase() + dateStr.slice(1)}</option>;
                      })}
                    </Select>
                  </InputGroup>

                  <InputGroup>
                    <Label>Horário</Label>
                    <Select 
                      value={scheduledTime || ''} 
                      onChange={(e) => setScheduledTime(e.target.value)}
                    >
                      <option value="">Selecione um horário</option>
                      <option value="17:00 - 17:30">17:00 - 17:30</option>
                      <option value="17:30 - 18:00">17:30 - 18:00</option>
                      <option value="18:00 - 18:30">18:00 - 18:30</option>
                      <option value="18:30 - 19:00">18:30 - 19:00</option>
                      <option value="19:00 - 19:30">19:00 - 19:30</option>
                    </Select>
                  </InputGroup>

                  <CheckoutButton 
                    onClick={() => setIsSchedulingModalOpen(false)}
                    disabled={!scheduledDate || !scheduledTime}
                  >
                    CONFIRMAR AGENDAMENTO
                  </CheckoutButton>
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {isChangeModalOpen && (
          <ModalOverlay zIndex={10200}>
            <ModalContent onClick={e => e.stopPropagation()} maxWidth="400px">
              <ModalBody>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2d3748', marginBottom: '1.5rem' }}>
                    Você vai pagar <strong>{formatCurrency(isTwoPayments ? (currentSelectingPayment === 1 || !paymentMethod2 ? paymentAmount1 : paymentAmount2) : total)}</strong> em dinheiro
                  </p>
                  <p style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2d3748', marginBottom: '2rem' }}>
                    Vai precisar de troco?
                  </p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                      style={{ flex: 1, padding: '1rem', borderRadius: '12px', backgroundColor: 'white', color: restaurant?.theme?.secondary || '#c0392b', border: `1px solid ${restaurant?.theme?.secondary || '#c0392b'}`, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => {
                        setNeedsChange(false);
                        setIsChangeModalOpen(false);
                      }}
                    >
                      NÃO
                    </button>
                    <button 
                      style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: 'none', backgroundColor: restaurant?.theme?.secondary || '#c0392b', color: 'white', fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => {
                        setNeedsChange(true);
                        // Show another state or input for amount
                      }}
                    >
                      SIM
                    </button>
                  </div>

                  {needsChange === true && (
                    <div style={{ marginTop: '2rem' }}>
                      <p style={{ fontWeight: 700, fontSize: '1.2rem', color: '#2d3748', marginBottom: '1rem' }}>Troco pra quanto?</p>
                      <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1.5rem' }}>Informe quanto você irá dar que calculamos o troco</p>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <CurrencyInput 
                          value={Number(changeFor)}
                          onChange={(val) => setChangeFor(val.toString())}
                          autoFocus
                        />
                      </div>
                      {Number(changeFor) > (isTwoPayments ? (currentSelectingPayment === 1 || !paymentMethod2 ? paymentAmount1 : paymentAmount2) : total) && (
                        <p style={{ fontWeight: 800, fontSize: '1.1rem', color: '#2d3748', marginBottom: '2rem' }}>
                          TROCO: <span style={{ color: '#2d3748' }}>{formatCurrency(Number(changeFor) - (isTwoPayments ? (currentSelectingPayment === 1 || !paymentMethod2 ? paymentAmount1 : paymentAmount2) : total))}</span>
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button 
                          style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: `1px solid ${restaurant?.theme?.secondary || '#c0392b'}`, backgroundColor: 'white', color: restaurant?.theme?.secondary || '#c0392b', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => setNeedsChange(null)}
                        >
                          VOLTAR
                        </button>
                        <button 
                          style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: 'none', backgroundColor: restaurant?.theme?.secondary || '#c0392b', color: 'white', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => setIsChangeModalOpen(false)}
                          disabled={!changeFor || Number(changeFor) <= (isTwoPayments ? (currentSelectingPayment === 1 || !paymentMethod2 ? paymentAmount1 : paymentAmount2) : total)}
                        >
                          CONFIRMAR
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {isChangeModalOpen2 && (
          <ModalOverlay zIndex={10200}>
            <ModalContent onClick={e => e.stopPropagation()} maxWidth="400px">
              <ModalBody>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2d3748', marginBottom: '1.5rem' }}>
                    Você vai pagar <strong>{formatCurrency(paymentAmount2)}</strong> em dinheiro (2ª Forma)
                  </p>
                  <p style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2d3748', marginBottom: '2rem' }}>
                    Vai precisar de troco?
                  </p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                      style={{ flex: 1, padding: '1rem', borderRadius: '12px', backgroundColor: 'white', color: restaurant?.theme?.secondary || '#c0392b', border: `1px solid ${restaurant?.theme?.secondary || '#c0392b'}`, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => {
                        setNeedsChange2(false);
                        setIsChangeModalOpen2(false);
                      }}
                    >
                      NÃO
                    </button>
                    <button 
                      style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: 'none', backgroundColor: restaurant?.theme?.secondary || '#c0392b', color: 'white', fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => {
                        setNeedsChange2(true);
                      }}
                    >
                      SIM
                    </button>
                  </div>

                  {needsChange2 === true && (
                    <div style={{ marginTop: '2rem' }}>
                      <p style={{ fontWeight: 700, fontSize: '1.2rem', color: '#2d3748', marginBottom: '1rem' }}>Troco pra quanto?</p>
                      <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1.5rem' }}>Informe quanto você irá dar que calculamos o troco</p>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <CurrencyInput 
                          value={Number(changeFor2)}
                          onChange={(val) => setChangeFor2(val.toString())}
                          autoFocus
                        />
                      </div>
                      {Number(changeFor2) > paymentAmount2 && (
                        <p style={{ fontWeight: 800, fontSize: '1.1rem', color: '#2d3748', marginBottom: '2rem' }}>
                          TROCO: <span style={{ color: '#2d3748' }}>{formatCurrency(Number(changeFor2) - paymentAmount2)}</span>
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button 
                          style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: `1px solid ${restaurant?.theme?.secondary || '#c0392b'}`, backgroundColor: 'white', color: restaurant?.theme?.secondary || '#c0392b', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => setNeedsChange2(null)}
                        >
                          VOLTAR
                        </button>
                        <button 
                          style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: 'none', backgroundColor: restaurant?.theme?.secondary || '#c0392b', color: 'white', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => setIsChangeModalOpen2(false)}
                          disabled={!changeFor2 || Number(changeFor2) <= paymentAmount2}
                        >
                          CONFIRMAR
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {showTwoPaymentsSelection && (
           <ModalOverlay zIndex={10100} onClick={() => setShowTwoPaymentsSelection(false)}>
            <ModalContent onClick={e => e.stopPropagation()} maxWidth="500px" noPadding>
              <ModalHeader sticky noBorder>
                <BackButton 
                  onClick={() => setShowTwoPaymentsSelection(false)} 
                  style={{ 
                    position: 'absolute', 
                    left: '1rem', 
                    background: 'white', 
                    borderRadius: '50%', 
                    width: '32px', 
                    height: '32px', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                    zIndex: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '1rem', color: '#2d3748' }}></i>
                </BackButton>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <ModalTitle>Total a pagar: {formatCurrency(total)}</ModalTitle>
                </div>
              </ModalHeader>
              <ModalBody padding="1.5rem">
                 {!isEditingAmount ? (
                   <>
                    <SectionTitle>Escolha a {currentSelectingPayment}ª forma de pagamento</SectionTitle>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {availablePaymentMethods.map(method => (
                          <PaymentOption 
                            key={method.id} 
                            selected={(currentSelectingPayment === 1 ? paymentMethod : paymentMethod2) === method.id}
                            onClick={() => {
                              if (currentSelectingPayment === 1) setPaymentMethod(method.id);
                              else setPaymentMethod2(method.id);
                              
                              if (method.id === PaymentMethod.CASH) {
                                if (currentSelectingPayment === 1) {
                                  setIsChangeModalOpen(true);
                                } else {
                                  setIsChangeModalOpen2(true);
                                }
                              }
                            }}
                          >
                            {renderPaymentMethodIcon(method.id)}
                            <PaymentOptionInfo>
                              <span>{method.name}</span>
                              {PAYMENT_METHOD_DESCRIPTIONS[method.id] && (
                                <span style={{ fontSize: '0.75rem', color: '#718096' }}>
                                  {PAYMENT_METHOD_DESCRIPTIONS[method.id]}
                                </span>
                              )}
                            </PaymentOptionInfo>
                            {(currentSelectingPayment === 1 ? paymentMethod : paymentMethod2) === method.id && <div className="check"><i className="fas fa-check"></i></div>}
                          </PaymentOption>
                        ))}
                      </div>
                    </>
                 ) : (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f7fafc', borderRadius: '12px', textAlign: 'center' }}>
                      <p style={{ fontWeight: 700, color: '#2d3748', marginBottom: '0.5rem' }}>Editando valor para:</p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        {renderPaymentMethodIcon(currentSelectingPayment === 1 ? paymentMethod : paymentMethod2, restaurant?.theme?.secondary || '#c0392b')}
                        <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          {currentSelectingPayment === 1 
                            ? getCheckoutPaymentMethodLabel(paymentMethod)
                            : getCheckoutPaymentMethodLabel(paymentMethod2)
                          }
                        </span>
                      </div>
                    </div>
                 )}

                  <SectionTitle style={{ marginTop: isEditingAmount ? '0' : '2rem' }}>Quanto deseja pagar?</SectionTitle>
                  <div style={{ marginBottom: '1.5rem' }}>
                      <CurrencyInput 
                        value={currentSelectingPayment === 1 ? paymentAmount1 : paymentAmount2}
                        onFocus={(e) => e.target.select()}
                        onChange={(val) => {
                           if (currentSelectingPayment === 1) {
                              setPaymentAmount1(val);
                              setPaymentAmount2(total - val);
                           } else {
                              setPaymentAmount2(val);
                              setPaymentAmount1(total - val);
                           }
                        }}
                      />
                  </div>

                  <CheckoutButton onClick={() => setShowTwoPaymentsSelection(false)}>
                    CONFIRMAR
                  </CheckoutButton>
              </ModalBody>
            </ModalContent>
           </ModalOverlay>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default CheckoutModal;
